import { useState, useEffect } from 'react'
import { Plane, MapPin, AlertTriangle, WifiOff } from 'lucide-react'
import { fetchSchedules } from '../lib/api'
import { getAirlineFromCallsign } from '../lib/utils'
import AirlineLogo from './AirlineLogo'

const STATUS_STYLE = {
  scheduled: { label: 'Scheduled', color: 'text-slate-400', bg: 'bg-slate-500/10' },
  active: { label: 'Departed', color: 'text-flight-cyan animate-pulse', bg: 'bg-flight-cyan/10' },
  landed: { label: 'Landed', color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  cancelled: { label: 'Cancelled', color: 'text-red-500', bg: 'bg-red-500/10' },
  incident: { label: 'Incident', color: 'text-red-400', bg: 'bg-red-500/10' },
  diverted: { label: 'Diverted', color: 'text-amber-400 animate-pulse', bg: 'bg-amber-500/10' },
}

function formatTime(iso) {
  if (!iso) return '--:--'
  return new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
}

export default function FIDSDisplay({ airport = 'LHR' }) {
  const [flights, setFlights] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    const load = () => {
      fetchSchedules(airport, 'departure').then(data => {
        if (!cancelled) { setFlights(data); setLoading(false) }
      })
    }
    load()
    const interval = setInterval(load, 60000)
    return () => { cancelled = true; clearInterval(interval) }
  }, [airport])

  return (
    <div className="h-screen w-screen bg-slate-950 flex flex-col">
      <div className="h-20 bg-slate-900 border-b-2 border-flight-cyan flex items-center px-8">
        <div className="flex items-center gap-4">
          <Plane className="w-8 h-8 text-flight-cyan" />
          <div>
            <h1 className="text-2xl font-bold text-white tracking-tight">{airport} DEPARTURES</h1>
            <p className="text-slate-400 text-sm">Live Flight Information Display System</p>
          </div>
        </div>
        <div className="ml-auto flex items-center gap-6">
          <div className="text-right">
            <div className="text-slate-500 text-xs">LOCAL TIME</div>
            <div className="text-white font-mono text-xl">{new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</div>
          </div>
          <div className="text-right">
            <div className="text-slate-500 text-xs">DATE</div>
            <div className="text-white font-mono">{new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}</div>
          </div>
        </div>
      </div>
      <div className="h-12 bg-slate-900/50 border-b border-slate-800 flex items-center px-8 text-slate-500 text-xs tracking-wider font-semibold">
        <div className="w-24">AIRLINE</div><div className="w-28">FLIGHT</div><div className="flex-1">DESTINATION</div><div className="w-24 text-center">SCHED</div><div className="w-24 text-center">ESTIM</div><div className="w-20 text-center">GATE</div><div className="w-20 text-center">TERM</div><div className="w-32 text-center">STATUS</div>
      </div>
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="h-full flex items-center justify-center">
            <div className="w-10 h-10 border-2 border-flight-cyan border-t-transparent rounded-full animate-spin" />
          </div>
        ) : !flights || flights.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-slate-500 gap-3">
            <WifiOff className="w-10 h-10" />
            <p className="text-sm">No live departure data available for {airport} right now</p>
          </div>
        ) : (
          flights.map((f, i) => {
            const style = STATUS_STYLE[f.status] || { label: f.status || 'Unknown', color: 'text-slate-400', bg: 'bg-slate-500/10' }
            const airlineMeta = getAirlineFromCallsign(f.flightNumber)
            return (
              <div key={i} className="h-16 flex items-center px-8 border-b border-slate-800/50 transition-colors hover:bg-slate-800/30">
                <div className="w-24 flex items-center gap-2">
                  <AirlineLogo iata={airlineMeta.iata} size="sm" />
                </div>
                <div className="w-28 text-white font-mono font-bold">{f.flightNumber || '---'}</div>
                <div className="flex-1 text-white text-sm flex items-center gap-2"><MapPin className="w-3 h-3 text-slate-500" />{f.airline}{f.otherAirport ? ` · ${f.otherAirport}` : ''}</div>
                <div className="w-24 text-center text-slate-400 font-mono">{formatTime(f.scheduledTime)}</div>
                <div className="w-24 text-center font-mono">
                  {f.estimatedTime && f.scheduledTime && f.estimatedTime !== f.scheduledTime
                    ? <span className="text-red-400">{formatTime(f.estimatedTime)}</span>
                    : <span className="text-slate-400">{formatTime(f.estimatedTime || f.scheduledTime)}</span>}
                </div>
                <div className="w-20 text-center"><span className="px-3 py-1 rounded-full bg-slate-800 text-white text-xs font-mono">{f.gate || '---'}</span></div>
                <div className="w-20 text-center text-slate-400 font-mono">{f.terminal || '---'}</div>
                <div className="w-32 text-center">
                  <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold ${style.bg} ${style.color}`}>
                    {f.status === 'cancelled' && <AlertTriangle className="w-3 h-3" />}{style.label}
                  </span>
                </div>
              </div>
            )
          })
        )}
      </div>
      <div className="h-10 bg-flight-cyan/10 border-t border-flight-cyan/30 flex items-center overflow-hidden">
        <div className="animate-marquee whitespace-nowrap flex items-center gap-8 text-flight-cyan text-sm font-mono px-4">
          <span>Welcome to {airport}</span><span>•</span><span>Live data via AviationStack</span><span>•</span><span>Please have boarding passes ready</span><span>•</span><span>Gate changes will be announced</span>
        </div>
      </div>
    </div>
  )
}
